import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";

import { Feature, useMap } from "@app/component/map/FeaturedMap";
import { setVisibleRegions } from "@app/scene/map/store/actions";
import {
  getBuckets,
  getDemographicData,
  getDemographicDataConfiguration,
  shouldVisualizeDemography,
} from "@app/scene/map/store/layers/boundaries/selectors";
import {
  DemographicDataConfiguration,
  RegionDemographicData,
} from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import Bucket from "@app/scene/map/store/layers/boundaries/types/Bucket";
import ClassificationMethod from "@app/scene/map/store/layers/boundaries/types/ClassificationMethod";
import DataSource from "@app/scene/map/store/layers/boundaries/types/DataSource";
import { getMapViewTool } from "@app/scene/map/store/selectors";
import DemographicData from "@app/scene/map/types/DemographicData";
import getLevelData from "@app/scene/map/utils/getLevelData";
import { RootState } from "@app/store/rootReducer";
import defaultIdGetter from "@app/util/defaultIdGetter";
import useMapDataEventHandler from "@app/util/hook/map/useMapDataEventHandler";

import OverlayContextMenu from "../../MapMode/components/OverlayContextMenu";
import areaRegionGeometryGetter from "../AreaRegionFeature/utils/areaRegionGeometryGetter";

import DemographyLabelFeature from "./components/DemographyLabelFeature";
import DemographyTooltip from "./components/DemographyTooltip";
import useStyleGetter from "./useStyleGetter";

export interface DemographicRegionItem extends AreaRegion, Partial<DemographicData> {
  color?: string;
  level: TerritoryLevel;
  visible?: boolean;
}

interface Props {
  buckets: Bucket[];
  currentLevel: TerritoryLevel;
  demographicData?: RegionDemographicData;
  demographicDataConfiguration: DemographicDataConfiguration;
  disableLabel?: boolean;
  level: TerritoryLevel;
  onSetVisibleRegions: typeof setVisibleRegions;
  regions: AreaRegion[];
  selectedTool?: MapTool;
  type?: string;
  visualizeDemography: boolean;
  zIndex?: number;
}

const DemographyFeature: React.FC<Props> = ({
  buckets,
  currentLevel,
  demographicData,
  demographicDataConfiguration,
  disableLabel,
  level,
  onSetVisibleRegions,
  regions,
  selectedTool,
  type = "demographicRegions",
  visualizeDemography,
  zIndex,
}) => {
  const { map } = useMap();
  const [hoveredRegion, setHoveredRegion] = useState<DemographicRegionItem | undefined>();
  const [contextMenuPosition, setContextMenuPosition] = useState<
    google.maps.LatLngLiteral | undefined
  >();
  const [tooltipPosition, setTooltipPosition] = useState<google.maps.LatLngLiteral | undefined>();
  const { classificationMethod, dataSource, onlyVisibleRegions } = demographicDataConfiguration;

  const handleHideMenu = useCallback(
    () => setContextMenuPosition(undefined),
    [setContextMenuPosition]
  );

  const handleMouseOver = useCallback(
    (region: DemographicRegionItem) => {
      if (visualizeDemography) {
        setHoveredRegion(region);
      }
    },
    [visualizeDemography]
  );

  const handleMouseMove = useCallback(
    (_, event: google.maps.MapMouseEvent) => {
      if (visualizeDemography || tooltipPosition) {
        setTooltipPosition(event.latLng?.toJSON());
      }
    },
    [tooltipPosition, visualizeDemography]
  );

  const handleMouseLeave = useCallback(() => {
    setTooltipPosition((oldPosition) => {
      if (oldPosition) {
        setHoveredRegion(undefined);
        return undefined;
      }
      return oldPosition;
    });
  }, []);

  const handleRightClick = useCallback(
    (region: DemographicRegionItem, event: google.maps.MapMouseEvent) => {
      setContextMenuPosition(event.latLng?.toJSON());
      setHoveredRegion(region);
    },
    []
  );

  useMapDataEventHandler(map, "mouseover", (event) => {
    if (visualizeDemography) {
      map.data.overrideStyle(event.feature, { strokeWeight: 4 });
    }
  });

  useMapDataEventHandler(map, "mouseout", (event) => {
    map.data.revertStyle(event.feature);
  });

  const regionStyleGetter = useStyleGetter(
    currentLevel,
    hoveredRegion,
    level,
    selectedTool,
    tooltipPosition,
    visualizeDemography,
    zIndex
  );

  const regionItems = useMemo(() => {
    const items: DemographicRegionItem[] = regions.map((region) => {
      const demographyData =
        currentLevel === level && demographicData
          ? getLevelData(demographicData, currentLevel)[region.id]
          : undefined;
      let color: HexColor | undefined;
      let visible: boolean | undefined;
      if (visualizeDemography && demographyData && buckets.length > 0) {
        const data =
          dataSource === DataSource.MEDIAN_INCOME
            ? demographyData.income.value
            : demographyData.population;
        buckets.forEach((bucket) => {
          if (
            bucket.range[0] < data ||
            bucket.range[0] === 0 ||
            (classificationMethod === ClassificationMethod.NATURAL_BREAKS &&
              bucket.range[0] <= data)
          ) {
            if ((bucket.range[1] && bucket.range[1] >= data) || !bucket.range[1]) {
              color = bucket.color;
              visible = bucket.visible;
            }
          }
        });
      }
      return { ...region, ...demographyData, color, level, visible };
    });
    return items;
  }, [
    buckets,
    classificationMethod,
    currentLevel,
    dataSource,
    demographicData,
    level,
    regions,
    visualizeDemography,
  ]);

  const viewportRegionItems: DemographicData["code"][] = useMemo(
    () => regions.map(({ id }) => id),
    [regions]
  );

  useEffect(() => {
    if (onlyVisibleRegions && viewportRegionItems.length) {
      onSetVisibleRegions(viewportRegionItems);
    }
  }, [onlyVisibleRegions, onSetVisibleRegions, viewportRegionItems]);

  return (
    <>
      <Feature<DemographicRegionItem>
        geometryGetter={areaRegionGeometryGetter}
        idGetter={defaultIdGetter}
        items={regionItems}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseOver={handleMouseOver}
        onRightClick={handleRightClick}
        styleGetter={regionStyleGetter}
        type={type}
      />
      <OverlayContextMenu
        onHide={handleHideMenu}
        onShowDemographyTooltip={setTooltipPosition}
        position={contextMenuPosition}
      />
      {visualizeDemography && !disableLabel && (
        <DemographyLabelFeature regionItems={regionItems} type={`${type}-labels`} />
      )}
      <DemographyTooltip demographicRegion={hoveredRegion} position={tooltipPosition} />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  buckets: getBuckets(state),
  demographicData: getDemographicData(state),
  demographicDataConfiguration: getDemographicDataConfiguration(state),
  selectedTool: getMapViewTool(state),
  visualizeDemography: shouldVisualizeDemography(state),
});

const mapDispatchToProps = {
  onSetVisibleRegions: setVisibleRegions,
};

export default connect(mapStateToProps, mapDispatchToProps)(DemographyFeature);
